import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';
import './AnimatedLabel.css';

/** Keep the control stable while its lettering and footprint transform. */
interface AnimatedLabelProps {
  text: string;
  className?: string;
  /** Reserve the longest known label so paired actions keep the same target. */
  reserveText?: string;
  /** Wrap notices and crossfade their copy while the height changes. */
  multiline?: boolean;
  /** Crossfade a bounded heading with a real ellipsis instead of moving clipped fragments. */
  truncate?: boolean;
}

/** Split at grapheme boundaries, keeping the shared beginning and ending intact. */
function sharedLettering(from: string, to: string) {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  const before = Array.from(segmenter.segment(from), part => part.segment);
  const after = Array.from(segmenter.segment(to), part => part.segment);
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start++;
  let end = 0;
  while (end < before.length - start && end < after.length - start
    && before[before.length - end - 1] === after[after.length - end - 1]) end++;
  return {
    prefix: before.slice(0, start).join(''),
    suffix: end ? before.slice(-end).join('') : '',
    before: before.slice(start, before.length - end).join(''),
    after: after.slice(start, after.length - end).join(''),
  };
}

/** Persistent text fragments retarget their horizontal motion during interrupted changes. */
function LetteringPiece({ text, kind, fromX, toX, wasVisible, visible, animate }: {
  text: string; kind: 'prefix' | 'middle' | 'suffix'; fromX: number; toX: number;
  wasVisible: boolean; visible: boolean; animate: boolean;
}) {
  const [entered, setEntered] = useState(!animate);
  useEffect(() => {
    if (!animate) return undefined;
    let second: number | undefined;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setEntered(true)); });
    return () => { cancelAnimationFrame(first); if (second !== undefined) cancelAnimationFrame(second); };
  }, [animate]);
  return <span className="animated-label-piece" data-label-piece={kind} data-text={text}
    style={{ transform: `translateX(${entered ? toX : fromX}px)`, opacity: (entered ? visible : wasVisible) ? 1 : 0 }} />;
}

/** Shared letters move to their new positions; only the edited fragment dissolves. */
function LetteringMorph({ from, to, reserveText, reduced }: { from: string; to: string; reserveText?: string; reduced: boolean }) {
  const parts = sharedLettering(from, to);
  const measures = useRef<HTMLSpanElement>(null);
  const [widths, setWidths] = useState([0, 0, 0, 0, 0]);
  useLayoutEffect(() => {
    const element = measures.current;
    if (!element) return undefined;
    const update = () => {
      const next = Array.from(element.children, child => child.getBoundingClientRect().width);
      setWidths(previous => next.every((width, index) => width === previous[index]) ? previous : next);
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    Array.from(element.children).forEach(child => observer?.observe(child));
    return () => observer?.disconnect();
  }, [from, to, reserveText, parts.prefix, parts.suffix]);
  const [beforeWidth, afterWidth, prefixWidth, suffixWidth, reservedWidth] = widths;
  const beforeX = reserveText ? (reservedWidth - beforeWidth) / 2 : 0;
  const afterX = reserveText ? (reservedWidth - afterWidth) / 2 : 0;
  const animate = from !== to && !reduced;
  return <span aria-hidden="true" inert className="animated-label-lettering">
    <span ref={measures} className="animated-label-fragment-measures">
      {[from, to, parts.prefix, parts.suffix, reserveText ?? to].map((value, index) => <span key={index} data-text={value} />)}
    </span>
    {parts.prefix && <LetteringPiece key={`prefix:${parts.prefix}`} kind="prefix" text={parts.prefix}
      fromX={beforeX} toX={afterX} wasVisible visible animate={animate} />}
    {[...new Set([parts.before, parts.after])].filter(Boolean).map(value => <LetteringPiece key={`middle:${value}`}
      kind="middle" text={value} fromX={beforeX + prefixWidth} toX={afterX + prefixWidth}
      wasVisible={value === parts.before} visible={value === parts.after} animate={animate} />)}
    {parts.suffix && <LetteringPiece key={`suffix:${parts.suffix}`} kind="suffix" text={parts.suffix}
      fromX={beforeX + beforeWidth - suffixWidth} toX={afterX + afterWidth - suffixWidth} wasVisible visible animate={animate} />}
  </span>;
}

/** Real copy supplies one accessible label; visual fragments never duplicate it. */
function LabelLayer({ text, current, animate, multiline }: { text: string; current: boolean; animate: boolean; multiline: boolean }) {
  const [entered, setEntered] = useState(!animate);
  useEffect(() => {
    if (!animate || !multiline) return undefined;
    let second: number | undefined;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setEntered(true)); });
    return () => { cancelAnimationFrame(first); if (second !== undefined) cancelAnimationFrame(second); };
  }, [animate, multiline]);
  return <span className={multiline ? 'animated-label-layer' : 'animated-label-readable sr-only'} aria-hidden={!current} inert={!current}
    data-phase={!current ? 'exiting' : entered ? 'current' : 'entering'}>{text}</span>;
}

/** Morph short action labels without a vertical swap; let longer notices dissolve in place. */
export function AnimatedLabel({ text, className = '', reserveText, multiline = false, truncate = false }: AnimatedLabelProps) {
  const reduced = useReducedMotion();
  const [layers, setLayers] = useState(() => [{ text, animate: false }]);
  const [change, setChange] = useState(() => ({ from: text, to: text }));
  const measure = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    setLayers(previous => reduced ? [{ text, animate: false }]
      : previous.some(layer => layer.text === text) ? previous : [...previous, { text, animate: true }]);
    setChange(previous => previous.to === text ? previous : { from: previous.to, to: text });
  }, [text, reduced]);

  useEffect(() => {
    if (reduced) return undefined;
    const timer = setTimeout(() => setLayers(previous => previous.filter(layer => layer.text === text)), 320);
    return () => clearTimeout(timer);
  }, [text, reduced]);

  useLayoutEffect(() => {
    const element = measure.current;
    if (!element) return undefined;
    const update = () => {
      const { width, height } = element.getBoundingClientRect();
      if (width <= 0 || height <= 0) {
        if (!text && !reserveText) setSize(previous => previous?.width === 0 ? previous : { width: 0, height: previous?.height ?? height });
        return;
      }
      setSize(previous => previous?.width === width && previous.height === height ? previous : { width, height });
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [text, reserveText, multiline]);

  return <span className={`animated-label ${className}`} data-multiline={multiline}
    style={size ? { width: multiline ? undefined : size.width, height: size.height } : undefined}>
    <span ref={measure} className="animated-label-measure" data-text={reserveText ?? text} aria-hidden="true" inert />
    {!multiline && !truncate && <LetteringMorph from={reduced ? text : change.from} to={change.to} reserveText={reserveText} reduced={reduced} />}
    {layers.map(layer => <LabelLayer key={layer.text} {...layer} multiline={multiline || truncate} current={layer.text === text} />)}
  </span>;
}
